// 던전 클리어 보상 카드(결과 화면 3장 중 1장): 골드만이 아니라 강화석·보석·장비·소비 아이템이 섞인다(2026-10-27 대표님 지적: "카드가 골드만 나온다").
// 순수 데이터·계산만(three·DOM import 금지). 게임 연결은 game/flow.ts(굴림·지급), 화면은 ui/rewardCards.ts.
//
// 경제 원칙(기존 수치를 깨지 않는다): 카드마다 기존 골드 굴림(지역 cardMin~cardMax × 스테이지 배율 × 난이도 배율)을 그대로 "예산"으로 쓰고,
// 골드가 아닌 카드는 그 예산을 같은 가치의 물건으로 바꾼 것이다(가치 환산 = 아래 GE 표). 등급(희귀도)이 높을수록 예산에 곱하는 배율이 커서
// 가끔 큰 보상이 나오지만 평균은 예산과 같다(RARITY_MULT·RARITY_WEIGHT, 시험이 지역·스테이지 종류별 평균 환산가치가 예산의 ±8% 안임을 고정한다).
// 한 판에 받는 건 3장 중 1장뿐이라, 모든 보상 합계는 예전 골드 카드 1장의 기대값과 같다. 전설 등급 장비는 카드에 없다(보스 확정 드롭 전용, c009 원칙 유지).
//
// 영수증 표현(src/game/save.ts RewardResult.cardLoot): 카드마다 문자열 하나. 이전 빌드는 모르는 필드로 그대로 두고, 이 필드가 없는 영수증은 전부 골드 카드다.
//   g:<등급>                      골드 카드 — 액수는 영수증 cards[i](골드가 아닌 카드는 cards[i] = 0이라 이전 빌드가 읽어도 이중 지급이 없다)
//   s:<등급>:<강화석 단계>:<개수>   강화석
//   m:<등급>:<보석 코드>:<개수>     보석
//   c:<등급>:<아이템 id>:<개수>     소비 아이템(물약·주문서)
//   e:<등급>:<장비 등급>:<정의 id>:<난이도 n|h|x>  장비(옵션은 영수증 id로 결정적으로 다시 굴린다. 난이도가 아이템 레벨을 정하므로 영수증 문자열만으로 지급 연산이 정해진다)
import { DIFFICULTY_ILVL_ADD, ENHANCE_GOLD_ILVL_GROWTH, ENHANCE_GOLD_PER_STONE, stoneTierForIlvl } from './economy';
import { GEM_COMBINE, GEM_COLORS, POTIONS, SCROLLS, STACK_CAP, gemCode, gemDropTier, shopMult } from './consumables';
import { CARD_APPLIED_PREFIX } from './loot';
import { REGIONS_90D } from './world';
import { equipDef, hashSeed, nextRand, rolledItem, rollGear, seedRng, type DropContext, type SeedRng } from '../gear';
import type { GearOp } from '../gearLedger';
import type { Difficulty, Grade, RegionNo, StageKind } from './types';

export type CardKind = 'gold' | 'stone' | 'gem' | 'cons' | 'gear';
/** 0 일반(흰) · 1 희귀(파랑) · 2 영웅(보라) · 3 전설(금) */
export type CardRarity = 0 | 1 | 2 | 3;
export const CARD_RARITY_NAME: readonly string[] = ['일반', '희귀', '영웅', '전설'];
/** 등급 빛 색(화면 테두리·광선). GRADE_BEAM과 같은 결(흰·파랑·보라·금) */
export const CARD_RARITY_COLOR: readonly string[] = ['#f2f4fa', '#4a8cff', '#b06bff', '#ffc63d'];

/** 골드·강화석·소비 아이템 카드의 등급 굴림. 평균 배율 = 1.008 */
export const RARITY_WEIGHT: readonly number[] = [60, 28, 10, 2];
export const RARITY_MULT: readonly number[] = [0.8, 1.1, 1.6, 3.0];

/** 장비 카드의 등급 굴림(전설 없음). 스테이지가 일반이면 장비 카드 자체가 없다 */
export const GEAR_GRADE_WEIGHT: Readonly<Record<'mid' | 'final', readonly [Grade, number][]>> = {
  // BALANCE-1028 2절: 영웅 15·17% → 5·8%(보라 희소성). 빠진 몫은 희귀로
  mid: [['common', 50], ['rare', 45], ['epic', 5]],
  final: [['common', 45], ['rare', 44], ['epic', 8], ['set', 3]],
};
/** 장비 등급 → 카드 등급(빛 색) */
export const GEAR_CARD_RARITY: Readonly<Record<Grade, CardRarity>> = { common: 0, rare: 1, epic: 2, set: 3, legend: 3 };
/** 장비 환산가치(그 지역 카드 골드 평균(스테이지 배율 전)의 배수). 상점 장비 가격(SHOP_PRICE.gear 800~1500)·분해값을 따른 가안 */
export const GEAR_GE: Readonly<Record<Grade, number>> = { common: 0.8, rare: 2.5, epic: 8, set: 22, legend: 22 };

/**
 * 카드 종류 가중치(합 100). 일반 스테이지는 골드·소비, 보스 스테이지는 강화석·보석·장비가 더해진다. 보석은 지역3부터(없으면 강화석으로).
 * 이 값은 경제 불변식으로 고정한다: 경력 시뮬(tools/sim/economy.ts, 카드 구성 반영 MODEL_SHIPPED)에서 골드가 아닌 카드는 골드 수입을 줄이고(장비 카드는 판매·분해값만 실현)
 * 강화석 공급을 늘리는데, 12~13주 구간 순수입 여유가 1% 남짓이라 골드 카드 비중이 일반 90%·중간보스 80%·지역보스 72%를 넘어 줄이면 economy.test(순수입 ≥ 0, 밴드 평균 타수 단조)가 깨진다.
 * 넣고 싶은 "하나를 잡았다" 연출은 비중이 아니라 화면(등급 빛·플립)이 맡는다. 비중을 바꾸면 반드시 economy.test와 아래 cardReward.test 기대 분포를 함께 본다
 */
export const KIND_WEIGHT: Readonly<Record<StageKind, Readonly<Record<CardKind, number>>>> = {
  normal: { gold: 90, stone: 0, cons: 10, gem: 0, gear: 0 },
  mid: { gold: 80, stone: 4, cons: 8, gem: 4, gear: 4 },
  final: { gold: 72, stone: 4, cons: 10, gem: 6, gear: 8 },
};
/** 보석 카드가 한 등급 위로 나올 확률(그 카드는 전설 빛) */
export const GEM_UP_CHANCE = 0.12;
/** 소비 카드 후보(등급별). 가격은 환산가치(GE)로 쓴다 */
const CONS_POOL: readonly (readonly string[])[] = [
  ['hp_s', 'mp_s'],
  ['hp_m', 'mp_m', 'scroll_return'],
  ['hp_l', 'mp_l', 'feather_revive'],
  ['feather_revive'],
];
/** 강화석 카드 환산 프리미엄: 강화석은 골드와 달리 강화 속도를 정하는 병목 재료라, 같은 가치(GE)로 바꾸면 공급이 늘어난다 — 환산가를 이만큼 높게 쳐서 개수를 줄인다(tools/sim/economy.ts 경력 시뮬로 보정) */
export const STONE_CARD_PREMIUM = 1.6;
const STONE_MAX = 40;
const CONS_MAX = 60;

export interface CardInfo {
  readonly kind: CardKind;
  readonly rarity: CardRarity;
  /** 강화석 단계·보석 코드(그 밖 0) */
  readonly code: number;
  /** 강화석·보석·소비 개수(골드·장비 0) */
  readonly n: number;
  /** 소비 아이템 id·장비 정의 id */
  readonly id: string;
  /** 장비 등급(장비만) */
  readonly grade: Grade | '';
  /** 장비 난이도(장비만) */
  readonly diff: Difficulty;
}

/** 장비 카드 영수증 문자열의 난이도 글자 */
const DIFF_OF: Readonly<Record<string, Difficulty>> = { n: 'normal', h: 'hard', x: 'hell' };
const DIFF_CH: Readonly<Record<Difficulty, string>> = { normal: 'n', hard: 'h', hell: 'x' };
const GRADES_OK = /^(?:common|rare|epic|set|legend)$/;
/** 영수증 문자열 → 카드 정보(형식이 틀리면 null) */
export function parseCardLoot(s: unknown): CardInfo | null {
  if (typeof s !== 'string' || s.length > 64) return null;
  const p = s.split(':');
  const r = Number(p[1]);
  if (!(r >= 0 && r <= 3) || !Number.isInteger(r)) return null;
  const rarity = r as CardRarity;
  const num = (v: string | undefined, hi: number): number => (v !== undefined && /^\d{1,4}$/.test(v) && Number(v) <= hi ? Number(v) : -1);
  switch (p[0]) {
    case 'g': return p.length === 2 ? { kind: 'gold', rarity, code: 0, n: 0, id: '', grade: '', diff: 'normal' } : null;
    case 's': { const t = num(p[2], 3), n = num(p[3], 9999); return p.length === 4 && t >= 0 && n > 0 ? { kind: 'stone', rarity, code: t, n, id: '', grade: '', diff: 'normal' } : null; }
    case 'm': { const c = num(p[2], GEM_COLORS.length * 5 - 1), n = num(p[3], 99); return p.length === 4 && c >= 0 && n > 0 ? { kind: 'gem', rarity, code: c, n, id: '', grade: '', diff: 'normal' } : null; }
    case 'c': { const n = num(p[3], 99); return p.length === 4 && n > 0 && /^[a-z_]{3,24}$/.test(p[2]) ? { kind: 'cons', rarity, code: 0, n, id: p[2], grade: '', diff: 'normal' } : null; }
    case 'e': return p.length === 5 && GRADES_OK.test(p[2]) && /^[A-Za-z0-9_.-]{1,40}$/.test(p[3]) && DIFF_OF[p[4]] ? { kind: 'gear', rarity, code: 0, n: 1, id: p[3], grade: p[2] as Grade, diff: DIFF_OF[p[4]] } : null;
    default: return null;
  }
}
/** 영수증 문자열 3개가 모두 올바른가(없음 = 이전 영수증 = true) */
export function validCardLoot(v: unknown): boolean {
  return v === undefined || (Array.isArray(v) && v.length === 3 && v.every((x) => parseCardLoot(x) !== null));
}

export interface CardRollInput {
  readonly region: RegionNo;
  readonly diff: Difficulty;
  readonly kind: StageKind;
  /** 카드 3장의 골드 예산(기존 굴림: 지역 카드 범위 × 스테이지·난이도·장비 골드 배율) */
  readonly budget: readonly number[];
  /**
   * 골드 카드로 나올 때의 골드 액수(기존 굴림: budget에 장비 골드 획득 옵션(goldMult)까지 곱한 값). 없으면 budget.
   * 골드가 아닌 카드 환산에는 goldMult를 곱하지 않는다(옵션 보너스가 강화석·보석·장비 공급을 부풀리지 않게)
   */
  readonly goldAmount?: readonly number[];
  /** 골드 예산의 범위(최소·최대). 골드 카드의 등급(빛 색)은 예산이 범위 안에서 얼마나 높은지로 정한다 */
  readonly budgetRange: readonly [number, number];
  /** 영수증 id(굴림 씨앗) */
  readonly seed: string;
  /** 장비 카드를 굴릴 드롭 문맥(영웅·난이도 포함). 없으면 장비 카드는 강화석으로 바뀐다 */
  readonly gearCtx?: DropContext | null;
}
export interface CardRoll { readonly loot: string[]; readonly gold: number[] }

function pickIndex(r: SeedRng, w: readonly number[]): number {
  let sum = 0;
  for (const x of w) sum += x;
  let x = nextRand(r) * sum;
  for (let i = 0; i < w.length; i++) { x -= w[i]; if (x < 0 && w[i] > 0) return i; }
  for (let i = w.length - 1; i >= 0; i--) if (w[i] > 0) return i;
  return 0;
}

/** 지금 지역·난이도 강화석 단계와 강화석 1개의 환산가치(강화 골드와 같은 값, economy.ENHANCE_GOLD_PER_STONE) */
export function stoneInfo(region: RegionNo, diff: Difficulty): { tier: number; ge: number } {
  const ilvl = REGIONS_90D[region - 1].levels[0] + DIFFICULTY_ILVL_ADD[diff];
  return { tier: stoneTierForIlvl(ilvl), ge: ENHANCE_GOLD_PER_STONE * Math.pow(ENHANCE_GOLD_ILVL_GROWTH, ilvl - 1) };
}
/** 소비 아이템 1개의 환산가치(물약은 고정 가격, 주문서는 상점 배율) */
export function consGe(id: string, region: RegionNo): number {
  const p = POTIONS.find((x) => x.id === id);
  if (p) return p.price;
  const s = SCROLLS.find((x) => x.id === id);
  return s ? Math.round(s.base * shopMult(region)) : 0;
}
/** 보석 코드 등급(1..5)의 환산가치: 3개 합성이 한 등급 위(합성 수수료 GEM_COMBINE) → 등급마다 3배 */
export function gemGe(tier: number): number { return GEM_COMBINE.gold[1] * Math.pow(GEM_COMBINE.need, tier - 1); }

/** 카드 3장을 굴린다(결정적: 같은 입력 = 같은 결과). 골드 카드가 한 장은 반드시 있다 */
export function rollCards(inp: CardRollInput): CardRoll {
  const loot: string[] = [], gold: number[] = [];
  const w = KIND_WEIGHT[inp.kind];
  const gemTier = gemDropTier(inp.region, inp.diff);
  const kinds: CardKind[] = ['gold', 'stone', 'cons', 'gem', 'gear'];
  const weights = kinds.map((k) => (k === 'gem' && gemTier <= 0 ? 0 : k === 'gear' && !inp.gearCtx ? 0 : w[k]));
  // 보석·장비가 막힌 몫은 강화석으로(지역1·2 보스 카드)
  weights[1] += (w.gem - weights[3]) + (w.gear - weights[4]);
  const { tier: stoneTier, ge: stoneGe } = stoneInfo(inp.region, inp.diff);
  const draft: { kind: CardKind; budget: number }[] = [];
  for (let i = 0; i < 3; i++) {
    const r = seedRng(hashSeed(`card|${inp.seed}|${i}`));
    const kind = kinds[pickIndex(r, weights)];
    let rarity = pickIndex(r, RARITY_WEIGHT) as CardRarity;
    const v = Math.max(1, inp.budget[i] ?? 1) * RARITY_MULT[rarity];
    draft.push({ kind, budget: inp.budget[i] ?? 0 });
    switch (kind) {
      case 'gold': {
        // 골드 카드는 기존 범위(지역 카드 범위 × 배율) 안의 값 그대로 — 등급(빛)만 범위 안 위치로 정한다(상위 12%·2%가 영웅·전설 빛)
        const amount = inp.goldAmount?.[i] ?? inp.budget[i] ?? 0;
        const [lo, hi] = inp.budgetRange, pct = hi > lo ? (amount - lo) / (hi - lo) : 0.5;
        rarity = pct >= 0.98 ? 3 : pct >= 0.88 ? 2 : pct >= 0.6 ? 1 : 0;
        loot.push('g:' + rarity); gold.push(Math.max(0, Math.round(amount)));
        break;
      }
      case 'stone': loot.push(`s:${rarity}:${stoneTier}:${Math.max(1, Math.min(STONE_MAX, Math.round(v / (stoneGe * STONE_CARD_PREMIUM))))}`); gold.push(0); break;
      case 'cons': {
        const pool = CONS_POOL[rarity];
        const id = pool[Math.min(pool.length - 1, Math.floor(nextRand(r) * pool.length))];
        const price = consGe(id, inp.region);
        loot.push(`c:${rarity}:${id}:${Math.max(1, Math.min(CONS_MAX, Math.round(v / price)))}`); gold.push(0);
        break;
      }
      case 'gem': {
        const color = Math.min(GEM_COLORS.length - 1, Math.floor(nextRand(r) * GEM_COLORS.length));
        const up = nextRand(r) < GEM_UP_CHANCE && gemTier < 5;
        loot.push(`m:${up ? 3 : 2}:${gemCode(color, gemTier + (up ? 1 : 0))}:1`); gold.push(0);
        break;
      }
      case 'gear': {
        const table = GEAR_GRADE_WEIGHT[inp.kind === 'final' ? 'final' : 'mid'];
        const grade = table[pickIndex(r, table.map((x) => x[1]))][0];
        const item = rollGear(() => nextRand(r), inp.gearCtx!, 'boss', 'preview', 0, { min: grade, max: grade });
        loot.push(`e:${GEAR_CARD_RARITY[item.grade]}:${item.grade}:${item.def}:${DIFF_CH[inp.diff]}`); gold.push(0);
        break;
      }
    }
  }
  // 골드 카드 보장: 하나도 없으면 예산이 가장 큰 카드를 골드로 되돌린다(고르는 재미 + 최소 보상 보장)
  if (!loot.some((x) => x.startsWith('g:'))) {
    let best = 0;
    for (let i = 1; i < 3; i++) if (draft[i].budget > draft[best].budget) best = i;
    loot[best] = 'g:0'; gold[best] = Math.max(0, Math.round(inp.goldAmount?.[best] ?? draft[best].budget));
  }
  return { loot, gold };
}

/** 카드 한 장의 환산가치(골드 GE). gold는 그 카드의 골드 액수 */
export function cardGe(desc: string, gold: number, region: RegionNo, diff: Difficulty, cardAvg: number): number {
  const c = parseCardLoot(desc);
  if (!c) return gold;
  switch (c.kind) {
    case 'gold': return gold;
    case 'stone': return c.n * stoneInfo(region, diff).ge * STONE_CARD_PREMIUM;
    case 'gem': return c.n * gemGe(c.code % 5 + 1);
    case 'cons': return c.n * consGe(c.id, region);
    case 'gear': return GEAR_GE[c.grade as Grade] * cardAvg * Math.pow(ENHANCE_GOLD_ILVL_GROWTH, DIFFICULTY_ILVL_ADD[diff]);
  }
}

/**
 * 보상 카드 지급 연산(장비 장부, 고정 id → 몇 번을 넣어도 한 번). 골드 카드는 연산이 없다(골드는 영수증 정산).
 * 장비는 영수증 id로 옵션을 결정적으로 다시 굴려 만든다(화면에 보인 정의·등급 그대로). 알 수 없는 정의면 null.
 */
export function cardGrantOp(resultId: string, desc: string): GearOp | null {
  const c = parseCardLoot(desc);
  if (!c || c.kind === 'gold') return null;
  const id = CARD_APPLIED_PREFIX + resultId;
  switch (c.kind) {
    case 'stone': return { id, t: 'stones', tier: c.code as 0 | 1 | 2 | 3, n: c.n };
    case 'gem': return { id, t: 'gems', code: c.code, n: c.n };
    case 'cons': return { id, t: 'grant', item: c.id, n: Math.min(c.n, STACK_CAP) };
    case 'gear': {
      const def = equipDef(c.id);
      if (!def) return null;
      const r = seedRng(hashSeed(`cardgear|${resultId}`));
      const item = rolledItem(() => nextRand(r), def, c.grade as Grade, def.level + DIFFICULTY_ILVL_ADD[c.diff], `cd-${resultId}`);
      return { id, t: 'pickup', item };
    }
    default: return null;
  }
}

// ── 화면 문구 ─────────────────────────────────────────
const STONE_NAME = ['강화석(하)', '강화석(중)', '강화석(상)', '강화석(최상)'];
const GEM_TIER = ['깨진', '흐린', '맑은', '빛나는', '완벽한'];
const GEM_NAME = GEM_COLORS.map((g) => g.name);
const CONS_NAME: Readonly<Record<string, string>> = {
  hp_s: 'HP 물약(소)', hp_m: 'HP 물약(중)', hp_l: 'HP 물약(대)', mp_s: 'MP 물약(소)', mp_m: 'MP 물약(중)', mp_l: 'MP 물약(대)',
  scroll_return: '귀환 주문서', feather_revive: '부활 깃털',
};
/** 카드 앞면 이름·수량 문구(장비 이름은 호출자가 equipDef로 찾아 넘긴다) */
export function cardTitle(desc: string, gold: number, gearName: (id: string) => string): { label: string; name: string; qty: string } {
  const c = parseCardLoot(desc);
  if (!c || c.kind === 'gold') return { label: '골드', name: '골드', qty: gold.toLocaleString('en-US') };
  switch (c.kind) {
    case 'stone': return { label: '강화석', name: STONE_NAME[c.code] ?? '강화석', qty: '×' + c.n };
    case 'gem': return { label: '보석', name: `${GEM_TIER[c.code % 5]} ${GEM_NAME[Math.floor(c.code / 5)] ?? '보석'}`, qty: '×' + c.n };
    case 'cons': return { label: '소비', name: CONS_NAME[c.id] ?? c.id, qty: '×' + c.n };
    case 'gear': return { label: '장비', name: gearName(c.id), qty: CARD_GRADE_NAME[c.grade as Grade] };
  }
}
const CARD_GRADE_NAME: Readonly<Record<Grade, string>> = { common: '일반', rare: '희귀', epic: '영웅', set: '세트', legend: '전설' };
/** 결과 문구용 한 줄(예: "강화석(중) ×5") */
export function cardLine(desc: string, gold: number, gearName: (id: string) => string): string {
  const t = cardTitle(desc, gold, gearName);
  const c = parseCardLoot(desc);
  if (!c || c.kind === 'gold') return t.qty + ' 골드';
  return c.kind === 'gear' ? `${t.qty} 장비 ${t.name}` : `${t.name} ${t.qty}`;
}
